import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { Subject } from 'rxjs';

interface DialogData {
  picture: string;
  providedCamera?: string;
  upload?: boolean;
}

@Component({
  selector: 'gc-camera',
  templateUrl: './camera.component.html',
  styleUrls: ['./camera.component.scss'],
  standalone: false,
})
export class CameraComponent implements OnInit {
  public cameraAvailable = false;
  public multipleWebcamsAvailable = false;
  private deviceId: string;
  private readonly errors: WebcamInitError[] = [];
  public usingCamera = false;
  public hasPicture = false;
  public hasCamera = false;
  public frontCameraActive = false;

  // Latest snapshot
  public webcamImage: WebcamImage;
  // Current profile picture
  public currentImage: string;

  // webcam snapshot trigger
  public readonly trigger$ = new Subject<void>();
  // switch to next / previous / specific webcam; true/false: forward/backwards, string: deviceId
  public readonly nextWebcam$ = new Subject<boolean | string>();

  public videoOptions: MediaTrackConstraints = { facingMode: { ideal: 'user' }, deviceId: { ideal: this.data.providedCamera } };

  @ViewChild('webcamChild') webcamChild: ElementRef;

  constructor(
    private readonly dialogRef: MatDialogRef<CameraComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogData,
    private readonly alert: AlertService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.trigger$.subscribe(() => {
      this.usingCamera = !this.usingCamera;
    });
    this.hasPicture = !!this.data.picture;
    this.hasCamera = !!this.data.providedCamera;
    this.currentImage = this.hasPicture ? this.data.picture : !this.hasCamera ? 'assets/images/avatar.svg' : '';
    this.getWebcamInfo();
    navigator.mediaDevices.addEventListener('devicechange', () => {
      this.getWebcamInfo();
    });
  }

  get backCamera() {
    return localStorage.getItem('kiosk_backcamera');
  }

  get toggleFrontCameraValue() {
    return localStorage.getItem('invert_front_camera_xaxis') === 'true';
  }

  get toggleBackCamera() {
    return localStorage.getItem('invert_back_camera_xaxis') === 'true';
  }

  async getWebcamInfo() {
    const mediaDevices = await WebcamUtil.getAvailableVideoInputs();
    this.multipleWebcamsAvailable = (mediaDevices?.length ?? 0) > 1;
    this.cameraAvailable = !!mediaDevices?.length;
    if (this.cameraAvailable) {
      if (this.deviceId !== this.backCamera) {
        this.cameraWasSwitched(this.backCamera);
        this.showNextWebcam(this.backCamera);
        this.invertCamera();
      }
    } else {
      this.deviceId = this.data.providedCamera;
    }
  }

  invertCamera() {
    const webcamElement = this.webcamChild?.nativeElement;
    if (webcamElement) {
      if (this.frontCameraActive) {
        if (!!this.toggleFrontCameraValue) {
          webcamElement.style.transform = 'scale(-1, 1)';
        } else {
          webcamElement.style.transform = 'scale(1, 1)';
        }
      } else {
        if (!!this.toggleBackCamera) {
          webcamElement.style.transform = 'scale(-1, 1)';
        } else {
          webcamElement.style.transform = 'scale(1, 1)';
        }
      }
    }
  }

  async onFileChange(files: FileList) {
    this.usingCamera = false;
    const file = files[0];
    this.webcamImage = new WebcamImage((await this.compressImage(file)).toString(), null, null);
  }

  async compressImage(file: File) {
    return new Promise<string>((resolve, reject) => {
      const width = 640;
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const scaleFactor = width / img.width;
        canvas.width = width;
        canvas.height = img.height * scaleFactor;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        ctx.drawImage(img, 0, 0, width, img.height * scaleFactor);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
    });
  }

  triggerSnapshot() {
    this.trigger$.next();
  }

  handleInitError(error: WebcamInitError) {
    if (error.mediaStreamError?.name === 'NotAllowedError') {
      this.alert.snackbar('camera_access_was_not_allowed_by_user_.');
      this.cameraAvailable = false;
    }
    this.errors.push(error);
  }

  showNextWebcam(directionOrDeviceId: boolean | string) {
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    this.nextWebcam$.next(directionOrDeviceId);
  }

  declineImage() {
    this.usingCamera = false;
    this.webcamImage = null;
  }

  acceptImage() {
    this.usingCamera = false;
    this.dialogRef.close(this.webcamImage.imageAsDataUrl);
  }

  resetImage() {
    const config = { hasBackdrop: true, data: { confirmText: this.translateService.instant('reset_photo') } };
    this.alert
      .messageDialog$(
        this.translateService.instant('remove_this_photo_title'),
        this.translateService.instant('remove_this_photo_message'),
        'error',
        true,
        config,
      )
      .subscribe((reset) => {
        if (reset) {
          this.data.picture = null;
          this.currentImage = 'assets/images/avatar.svg';
          this.dialogRef.close(null);
        }
      });
  }

  cameraWasSwitched(deviceId: string) {
    this.deviceId = deviceId;
    this.frontCameraActive = !this.frontCameraActive;
    this.invertCamera();
  }

  cameraUnavailable() {
    const title = this.translateService.instant('camera_unavailable');
    const message = this.translateService.instant('no_available_camera_found_message');
    this.alert.messageDialog$(title, message, 'error');
  }
}
